/* eslint-disable no-restricted-syntax */
import classNames from "components/ui/classNames";
import Link from "next/link";
import { invariant } from "outvariant";
import type { ReactElement, ReactNode, RefObject } from "react";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useIntersectionObserver } from "usehooks-ts";

type Callback = () => void;

const collectedRefs = new Map<string, RefObject<HTMLElement>>();
let listeners: Callback[] = [];

function getSnapshotById(id: string) {
  return () => {
    return collectedRefs.get(id);
  };
}

function subscribe(listener: Callback) {
  listeners = [...listeners, listener];
  return () => {
    listeners = listeners.filter((candidate) => candidate !== listener);
  };
}

function notifyAll() {
  for (const listener of listeners) {
    listener();
  }
}

interface SpanPixelBeaconProps extends React.HTMLProps<HTMLElement> {
  id: string;
  debug?: boolean;
  offset?: number;
}
export function SpanPixelBeacon({ id, debug, offset, ...props }: SpanPixelBeaconProps) {
  invariant(id, "The `id` is required for a `SpanPixelBeacon`");
  const ref = useRef<HTMLElement>(null);
  collectedRefs.set(id, ref);

  useEffect(() => {
    if (ref.current) {
      collectedRefs.set(id, ref);
      setTimeout(() => {
        notifyAll();
      }, 1);
    }

    return () => {
      if (!ref.current) {
        collectedRefs.delete(id);
        notifyAll();
      }
    };
  }, []);
  return (
    <span
      className={classNames("absolute inset-0 h-px w-px", {
        "z-50 h-10 w-10 bg-red-600": debug && process.env.FUNDAMENTEI_ENV !== "production",
      })}
      style={{
        top: offset,
      }}
      ref={ref}
      {...props}
    />
  );
}

interface SectionBeaconProps extends React.HTMLProps<HTMLElement> {
  id: string;
}
export function SectionBeacon({ id, ...props }: SectionBeaconProps) {
  invariant(id, "The `id` is required for a `SectionBeacon`");
  const ref = useRef<HTMLElement>(null);
  collectedRefs.set(id, ref);

  useEffect(() => {
    if (ref.current) {
      collectedRefs.set(id, ref);
      setTimeout(() => {
        notifyAll();
      }, 1);
    }

    return () => {
      if (!ref.current) {
        collectedRefs.delete(id);
        notifyAll();
      }
    };
  }, []);
  return <section ref={ref} {...props} />;
}

interface ScrollAvoidanceNodeProps {
  id: string;
  children: (isVisible: boolean) => ReactElement | null;
}
export function ScrollAvoidanceNode({ id, children }: ScrollAvoidanceNodeProps) {
  invariant(id, "The `id` is required for a `ScrollJumpButton`");

  const isVisible = useSyncExternalStore(subscribe, getSnapshotById(id), getSnapshotById(id));
  return children(!!isVisible);
}

interface ScrollJumpButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  id: string;
  alwaysVisible?: boolean;
  options?: ScrollIntoViewOptions;
}
export function ScrollJumpButton({
  id,
  alwaysVisible = false,
  options = {
    inline: "start",
    block: "start",
  },
  ...props
}: ScrollJumpButtonProps) {
  invariant(id, "The `id` is required for a `ScrollJumpButton`");

  const match = useSyncExternalStore(subscribe, getSnapshotById(id), getSnapshotById(id));
  if (!match && !alwaysVisible) {
    return null;
  }

  return (
    <button
      type="button"
      {...props}
      disabled={match === null}
      className={classNames(
        "select-none",
        "rounded-full bg-white px-4 py-1 text-sm font-medium text-slate-600",
        "hover:bg-stone-100 hover:font-medium hover:text-stone-800",
        "disabled:pointer-events-none disabled:opacity-70",
        "border border-stone-200 group-data-[stuck]:border-stone-300",
      )}
      onClick={() => {
        if (match && match.current) {
          match.current.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
            ...options,
          });
        }
      }}
    />
  );
}

interface ScrollSkipButtonProps {
  href: string;
  children: ReactNode;
}
export function ScrollSkipButton({ href, children, ...props }: ScrollSkipButtonProps) {
  return (
    <Link
      href={href}
      className={classNames(
        "select-none",
        "rounded-full bg-white px-4 py-1 text-sm font-medium text-slate-600",
        "hover:bg-stone-100 hover:font-medium hover:text-stone-800",
        "disabled:pointer-events-none disabled:opacity-70",
        "border border-stone-200 group-data-[stuck]:border-stone-300",
      )}
      {...props}
    >
      {children}
    </Link>
  );
}

interface StickyNavbarJumpersProps {
  children: ReactNode;
}
export function StickyNavbarJumpers({ children }: StickyNavbarJumpersProps) {
  // const ref = useRef<HTMLDivElement>(null);
  const { entry, ref } = useIntersectionObserver({
    rootMargin: "-72px 0px 0px 0px",
    threshold: [1],
  });
  // https://stackoverflow.com/a/73436174
  // NOTE: Watch out for https://ishadeed.com/article/css-state-queries/
  // const isStickyReady = ref.current !== null;
  const isPinned = !entry?.isIntersecting;

  return (
    <>
      {/* Sensor. Avoid tailwind because the layout is delayed */}
      <div style={{ position: "absolute", width: 1, height: 1 }} ref={ref} />
      <div
        data-stuck={isPinned || undefined}
        className={classNames(
          { sticky: true },
          "top-16 z-10 rounded-b-xl border border-transparent text-center",
          "data-[stuck]:border-stone-300 data-[stuck]:bg-stone-200 data-[stuck]:shadow-md",
          "lg:top-20",
          "group",
        )}
      >
        <nav className="inline-flex flex-wrap gap-4 rounded-xl px-4 py-2.5">{children}</nav>
      </div>
    </>
  );
}
